import { Inject, Injectable, OnDestroy } from '@angular/core'
import { Subject, takeUntil } from 'rxjs'
import { MapService } from 'src/app/core/services/map.service'
import { ExpressionSpecification, SymbolLayerSpecification } from 'maplibre-gl'
import { DataClusteredSignsService } from '../services/data-clustered-signs.service'

import { emptyGeoJsonSource } from 'src/app/core/utils/source-utils'
import { AppStateService } from 'src/app/core/services/app-state.service'
import { NavigationStart, Router } from '@angular/router'
import { MapEventsService } from 'src/app/core/services/map-events.service'
import { MapSignsIcons, MapSupsIcons } from 'src/app/core/enums/map-icons.enum'
import { withSignTypeFilter } from '../utils/property-filter.utils'

@Injectable()
export class LayerClusteredSignsService implements OnDestroy {
  public isLayerVisible = false
  private cleanUp$ = new Subject<void>()
  private timestamp: Date | undefined
  public selectedFeatureId: string = ''
  public onDestroy: boolean = false

  public layerId = this.layer
  public sourceId = `${this.layerId}-src`
  public layerSubsSymbolsId = `${this.layer}-subs-symbols`

  private clusteredSignsBaseFilter: ExpressionSpecification = ['>', ['get', 'probability'], 0]
  private clusteredSignsSubTypesBaseFilter: ExpressionSpecification = [
    'all',
    ['!', ['has', 'point_count']],
    ['>', ['get', 'probability'], 0],
    ['>', ['length', ['get', 'supplementalSign']], 0],
  ]

  private clusteredSignsLayerSpecification: SymbolLayerSpecification = {
    id: `${this.layer}`,
    source: this.sourceId,
    minzoom: this.minZoom,
    type: 'symbol',
    filter: this.clusteredSignsBaseFilter,
    layout: {
      'icon-rotation-alignment': 'map',
      'icon-ignore-placement': true,
      'icon-anchor': 'bottom',
      'icon-allow-overlap': true,
      'icon-rotate': ['get', 'heading'],
      // Use conditional expression for symbol-sort-key
      'symbol-sort-key': [
        'case',
        this.getSelectedFeatureExpression(this.selectedFeatureId),
        1000000000000, // High value to bring selected feature to front
        0,
      ],

      'icon-image': [
        'case',
        // Speed limit signs with numeric values
        ['==', ['get', 'mainType'], MapSignsIcons.SPEED_LIMIT],
        [
          'case',
          ['==', ['get', 'displayType'], 'digital'],
          [
            'case',
            ['==', ['get', 'speedLimit'], null],
            ['concat', MapSignsIcons.SPEED_LIMIT, '_NULL_DIGITAL_MEDIUM'],
            ['concat', MapSignsIcons.SPEED_LIMIT, '_', ['get', 'speedLimit'], '_DIGITAL_MEDIUM'],
          ],
          ['concat', MapSignsIcons.SPEED_LIMIT, '_', ['get', 'speedLimit'], '_MEDIUM'],
        ],
        // Speed limit end signs
        ['==', ['get', 'mainType'], MapSignsIcons.SPEED_LIMIT_END],
        [
          'case',
          ['==', ['get', 'displayType'], 'digital'],
          ['concat', MapSignsIcons.SPEED_LIMIT_END, '_', ['get', 'speedLimit'], '_DIGITAL_MEDIUM'],
          ['concat', MapSignsIcons.SPEED_LIMIT_END, '_', ['get', 'speedLimit'], '_MEDIUM'],
        ],

        // Speed limit zone signs
        ['==', ['get', 'mainType'], MapSignsIcons.SPEED_LIMIT_ZONE],
        [
          'case',
          ['==', ['get', 'displayType'], 'digital'],
          ['concat', MapSignsIcons.SPEED_LIMIT_ZONE, '_', ['get', 'speedLimit'], '_DIGITAL_MEDIUM'],
          ['concat', MapSignsIcons.SPEED_LIMIT_ZONE, '_', ['get', 'speedLimit'], '_MEDIUM'],
        ],

        // Speed limit zone end signs
        ['==', ['get', 'mainType'], MapSignsIcons.SPEED_LIMIT_ZONE_END],
        [
          'case',
          ['==', ['get', 'displayType'], 'digital'],
          [
            'concat',
            MapSignsIcons.SPEED_LIMIT_ZONE_END,
            '_',
            ['get', 'speedLimit'],
            '_DIGITAL_MEDIUM',
          ],
          ['concat', MapSignsIcons.SPEED_LIMIT_ZONE_END, '_', ['get', 'speedLimit'], '_MEDIUM'],
        ],

        ['==', ['get', 'mainType'], MapSignsIcons.YIELD],
        [
          'case',
          ['==', ['get', 'displayType'], 'digital'],
          ['concat', MapSignsIcons.YIELD, '_DIGITAL_MEDIUM'],
          ['concat', MapSignsIcons.YIELD, '_MEDIUM'],
        ],

        // Simple icon mappings (no concatenation needed)
        ['==', ['get', 'mainType'], MapSignsIcons.ALL_RESTRICTIONS_END],
        [
          'case',
          ['==', ['get', 'displayType'], 'digital'],
          ['concat', MapSignsIcons.ALL_RESTRICTIONS_END, '_DIGITAL_MEDIUM'],
          ['concat', MapSignsIcons.ALL_RESTRICTIONS_END, '_MEDIUM'],
        ],

        ['==', ['get', 'mainType'], MapSignsIcons.CITY_START],
        ['concat', MapSignsIcons.CITY_START, '_MEDIUM'],

        ['==', ['get', 'mainType'], MapSignsIcons.CITY_END],
        ['concat', MapSignsIcons.CITY_END, '_MEDIUM'],

        ['==', ['get', 'mainType'], MapSignsIcons.HIGHWAY],
        ['concat', MapSignsIcons.HIGHWAY, '_MEDIUM'],

        ['==', ['get', 'mainType'], MapSignsIcons.HIGHWAY_END],
        ['concat', MapSignsIcons.HIGHWAY_END, '_MEDIUM'],

        ['==', ['get', 'mainType'], MapSignsIcons.MOTORWAY],
        ['concat', MapSignsIcons.MOTORWAY, '_MEDIUM'],

        ['==', ['get', 'mainType'], MapSignsIcons.MOTORWAY_END],
        ['concat', MapSignsIcons.MOTORWAY_END, '_MEDIUM'],

        ['==', ['get', 'mainType'], MapSignsIcons.TRAFFIC_CALMING],
        ['concat', MapSignsIcons.TRAFFIC_CALMING, '_MEDIUM'],

        ['==', ['get', 'mainType'], MapSignsIcons.TRAFFIC_CALMING_END],
        ['concat', MapSignsIcons.TRAFFIC_CALMING_END, '_MEDIUM'],

        // Default fallback for unknown types
        MapSignsIcons.UNKNOWN,
      ],
      'icon-size': [
        'interpolate',
        ['linear'],
        ['zoom'],
        5,
        ['case', this.getSelectedFeatureExpression(this.selectedFeatureId), 0.15, 0.12],
        7,
        ['case', this.getSelectedFeatureExpression(this.selectedFeatureId), 0.25, 0.2],
        13,
        ['case', this.getSelectedFeatureExpression(this.selectedFeatureId), 0.6, 0.5],
        18,
        ['case', this.getSelectedFeatureExpression(this.selectedFeatureId), 1, 0.8],
      ],
    },
    paint: {
      'icon-opacity': 1.0,
    },
  }

  private clusteredSignsSubTypesLayerSpecification: SymbolLayerSpecification = {
    id: this.layerSubsSymbolsId,
    source: this.sourceId,
    minzoom: this.minZoom,
    type: 'symbol',
    filter: this.clusteredSignsSubTypesBaseFilter,
    layout: {
      'icon-rotation-alignment': 'map',
      'icon-rotate': ['get', 'heading'],
      'icon-anchor': 'bottom',
      'icon-ignore-placement': false,
      'symbol-sort-key': [
        'case',
        this.getSelectedFeatureExpression(this.selectedFeatureId),
        1000000000000, // High value to bring selected feature to front
        0,
      ],
      'icon-allow-overlap': true,
      'icon-image': [
        'case',
        // Single supplemental sign - use different icon
        ['==', ['length', ['get', 'supplementalSign']], 1],
        ['concat', ['get', 'type', ['at', 0, ['get', 'supplementalSign']]], '_MEDIUM'],
        // Multiple supplemental signs - use existing unknown icon
        [
          'concat',
          MapSupsIcons.SUP_UNKNOWN,
          '_',
          ['to-string', ['length', ['get', 'supplementalSign']]],
          '_MEDIUM',
        ],
      ],
      'icon-size': [
        'interpolate',
        ['linear'],
        ['zoom'],
        5,
        ['case', this.getSelectedFeatureExpression(this.selectedFeatureId), 0.15, 0.12],
        7,
        ['case', this.getSelectedFeatureExpression(this.selectedFeatureId), 0.25, 0.2],
        13,
        ['case', this.getSelectedFeatureExpression(this.selectedFeatureId), 0.6, 0.5],
        18,
        ['case', this.getSelectedFeatureExpression(this.selectedFeatureId), 1, 0.8],
      ],
      'icon-offset': [0, 55],
    },
    paint: {
      'icon-opacity': 1.0,
    },
  }
  constructor(
    private router: Router,
    private readonly mapService: MapService,
    private readonly mapEventsService: MapEventsService,
    private appState: AppStateService,
    @Inject(String) private layer: string,
    @Inject(Number) private minZoomAvailable: number,
    @Inject(Number) private maxZoomAvailable: number,
    @Inject(Number) private minZoom: number,
    private dataService: DataClusteredSignsService,
  ) {
    this.selectedFeatureId = this.appState.getState().selectedFeatureId ?? ''
    if (mapService.isMapReady) {
      this.initializeLayer()
    } else {
      this.mapService.mapReadyChanged.subscribe(() => {
        this.initializeLayer()
      })
    }
    this.appState.stateChanged.pipe(takeUntil(this.cleanUp$)).subscribe((state) => {
      this.evaluateState()
    })

    // NgOnDestroy is not working on instances created by a factory, therefore we need this workaround
    this.router.events.pipe(takeUntil(this.cleanUp$)).forEach((event) => {
      if (event instanceof NavigationStart) {
        this.ngOnDestroy()
      }
    })
  }

  ngOnDestroy() {
    this.onDestroy = true
    this.cancelRequests()
    this.cleanUp$.next()
    this.mapEventsService.detachMapEvents(this)
    this.mapService.map.removeLayer(this.layerSubsSymbolsId)
    this.mapService.map.removeLayer(this.layerId)
    this.mapService.map.removeSource(this.sourceId)
  }

  private initializeLayer() {
    this.mapService.map.addSource(this.sourceId, { ...emptyGeoJsonSource, promoteId: 'id' })
    this.mapService.map.addLayer(this.clusteredSignsSubTypesLayerSpecification)
    this.mapService.map.addLayer(this.clusteredSignsLayerSpecification)

    this.mapEventsService.registerForMapEvents(this, true)
    this.evaluateState()
  }

  public async reload() {
    this.cancelRequests()
    if (
      this.mapService.isMapReady &&
      this.isLayerVisible &&
      this.mapService.map.getZoom() >= this.minZoom
    ) {
      const xyzTiles = this.mapService.getXyzTiles(this.maxZoomAvailable, this.minZoomAvailable)
      const timestamp = this.appState.selectTimestamp()
      await this.dataService.loadClusteredSignsData(
        this.appState.isHistoryEnabled(),
        xyzTiles,
        timestamp,
        this.mapService.getExpandedBounds(0.5),
      )
      this.updateLayer()
    }
  }

  public updateLayer() {
    if (this.mapService.isMapReady) {
      this.mapService.map.removeFeatureState({
        source: this.sourceId,
        sourceLayer: this.sourceId,
      })

      const observations = this.dataService.getClusteredSigns(this.selectedFeatureId)
      this.changeSelectAndHoverStateOfSelectedFeature()
      this.mapService.setGeoJsonData(this.sourceId, observations)
    }
  }

  private evaluateState() {
    if (this.appState.selectLayers().some((layer) => layer === this.layer)) {
      this.setVisibility(true)
    } else {
      this.setVisibility(false)
    }

    // if layer is not visible stop all events
    if (this.isLayerVisible === false) return

    this.applySignTypeFilter()

    if (!this.timestamp) this.timestamp = this.appState.selectTimestamp()

    if (!this.dataService.isDataInitialized) {
      this.reload()
    }

    if (
      this.appState.hasLayerVisibilityChanged(this.layer) &&
      !this.appState.getState().historyEnabled
    ) {
      this.reload()
    }

    if (this.appState.hasMapBoundsChanged() && !this.appState.getState().historyEnabled) {
      this.reload()
    }

    if (this.appState.hasTimestampChanged()) {
      this.timestamp = this.appState.selectTimestamp()
      this.reload()
    }

    if (
      this.appState.hasSelectedFeatureIdChanged() &&
      this.appState.getState().selectedFeatureId !== ''
    ) {
      this.selectedFeatureId = this.appState.getState().selectedFeatureId || ''
      this.updateLayer()
      this.changeSelectAndHoverStateOfSelectedFeature()
    }
  }

  private applySignTypeFilter() {
    const selectedSignTypes = this.appState.selectSignTypes()
    const propertyFilters = this.appState.selectPropertyFilters()

    if (this.mapService.map.getLayer(this.layerId)) {
      this.mapService.map.setFilter(
        this.layerId,
        withSignTypeFilter(this.clusteredSignsBaseFilter, selectedSignTypes, propertyFilters),
      )
    }

    if (this.mapService.map.getLayer(this.layerSubsSymbolsId)) {
      this.mapService.map.setFilter(
        this.layerSubsSymbolsId,
        withSignTypeFilter(
          this.clusteredSignsSubTypesBaseFilter,
          selectedSignTypes,
          propertyFilters,
        ),
      )
    }
  }

  setVisibility(isVisible: boolean): void {
    this.isLayerVisible = isVisible
    if (this.mapService.isMapReady) {
      if (this.mapService.getLayerVisibilty(this.layer) != this.isLayerVisible) {
        this.mapService.setLayerVisibility(this.layerId, isVisible)
        this.mapService.setLayerVisibility(this.layerSubsSymbolsId, isVisible)

        if (!isVisible) {
          this.cancelRequests()
        }
      }
    }
  }

  // Update your cancelRequests method to properly cancel HTTP requests
  private cancelRequests() {
    // Cancel requests in the data service
    this.dataService.cancelCurrentRequests()
  }

  private changeSelectAndHoverStateOfSelectedFeature() {
    if (this.onDestroy) return
    this.mapService.map.setLayoutProperty(this.layerId, 'icon-size', [
      'interpolate',
      ['linear'],
      ['zoom'],
      5,
      ['case', this.getSelectedFeatureExpression(this.selectedFeatureId), 0.15, 0.12],
      7,
      ['case', this.getSelectedFeatureExpression(this.selectedFeatureId), 0.25, 0.2],
      13,
      ['case', this.getSelectedFeatureExpression(this.selectedFeatureId), 0.6, 0.5],
      18,
      ['case', this.getSelectedFeatureExpression(this.selectedFeatureId), 1.0, 0.8],
    ])
    this.mapService.map.setLayoutProperty(this.layerId, 'symbol-sort-key', [
      'case',
      this.getSelectedFeatureExpression(this.selectedFeatureId),
      1000000000000, // High value to bring selected feature to front
      0,
    ])

    this.mapService.map.setLayoutProperty(this.layerSubsSymbolsId, 'symbol-sort-key', [
      'case',
      this.getSelectedFeatureExpression(this.selectedFeatureId),
      1000000000000, // High value to bring selected feature to front
      0,
    ])

    this.mapService.map.setLayoutProperty(this.layerSubsSymbolsId, 'icon-size', [
      'interpolate',
      ['linear'],
      ['zoom'],
      5,
      ['case', this.getSelectedFeatureExpression(this.selectedFeatureId), 0.15, 0.12],
      7,
      ['case', this.getSelectedFeatureExpression(this.selectedFeatureId), 0.25, 0.2],
      13,
      ['case', this.getSelectedFeatureExpression(this.selectedFeatureId), 0.6, 0.5],
      18,
      ['case', this.getSelectedFeatureExpression(this.selectedFeatureId), 1.0, 0.8],
    ])
    this.mapService.map.setLayoutProperty(this.layerSubsSymbolsId, 'icon-offset', [0, 25])
    this.mapService.map.setLayoutProperty(this.layerSubsSymbolsId, 'text-offset', [
      'case',
      this.getSelectedFeatureExpression(this.selectedFeatureId),
      ['literal', [0, 1.2]], // Selected feature: text positioned above icon
      ['literal', [0, 1]], // Non-selected feature: text positioned on icon
    ])
  }

  private getSelectedFeatureExpression(selectedFeatureId: string): ExpressionSpecification {
    return ['any', ['==', ['to-string', ['get', 'id']], selectedFeatureId || '']]
  }
}
